// src/components/ServiceOrderPreviewModal.js
//
// v3.5.99.5 — Inline Service Order preview modal.
//
// Workflow:
//   1. Quote page mounts modal with `open` toggled by a "Preview Service
//      Order" button on the Documents card.
//   2. On open, POSTs to /.netlify/functions/buildSignatureDocument with
//      { quoteId, docType, countersignRequired: false }. The same backend
//      endpoint that proposal.js#maybeAutoFireServiceOrder and
//      sendServiceOrder.js use to build the SignWell-bound document — so
//      what the rep sees here is EXACTLY what will be sent to the client
//      (including any addendums attached via v3.5.99.4/v3.5.99.4.1).
//   3. Backend returns { pdf_base64, html, name, ... }. We prefer pdf_base64
//      (rendered via PDFShift per v3.5.98.0), fall back to wrapping the HTML
//      in a Blob if PDF is unavailable.
//   4. The modal renders the PDF inline via a Blob URL → iframe pattern.
//      Blob URLs have no size limit (vs data URIs which Chrome caps at ~2MB).
//      Object URL is revoked on modal close + on subsequent rebuilds so
//      we don't leak memory across multiple open/close cycles.
//
// Error handling:
//   - 400 'rates_not_locked' → friendly amber alert directing the rep to
//     lock OOS rates via the MarketRateCard.
//   - Other 4xx/5xx → red error with a Retry button that re-runs the fetch.
//   - Network error → same as above.
//
// Download:
//   - Browser's PDF viewer in the iframe usually exposes its own download
//     button. We also include an explicit Download button in the modal
//     header so reps don't have to hunt through the iframe chrome.

import { useState, useEffect, useRef } from 'react';

export default function ServiceOrderPreviewModal({ open, onClose, quoteId, docType, clientBiz }) {
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [blobUrl,   setBlobUrl]   = useState(null);
  const [filename,  setFilename]  = useState('Service-Order-Preview.pdf');
  // Track the most recent blob URL so we can revoke it cleanly on cleanup.
  // Direct ref vs state because useEffect cleanup runs AFTER state is updated.
  const urlRef = useRef(null);

  const buildPreview = async () => {
    // Revoke any previous blob URL before kicking off a new fetch.
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setBlobUrl(null);
    setError(null);
    setErrorCode(null);
    setLoading(true);

    try {
      const resp = await fetch('/.netlify/functions/buildSignatureDocument', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quoteId,
          docType,
          // Always preview without countersign block — saves a render pass
          // when the rep is just sanity-checking content; the actual send
          // path (sendServiceOrder.js / proposal.js) will set this
          // appropriately based on quote_type/admin settings when sending.
          countersignRequired: false,
        }),
      });

      const text = await resp.text();
      let json;
      try { json = JSON.parse(text); } catch { json = { error: text }; }

      if (!resp.ok) {
        setError(json.error || `Server returned ${resp.status}`);
        setErrorCode(json.code || null);
        setLoading(false);
        return;
      }

      // Prefer PDF if available; fall back to HTML wrapped in a Blob so
      // the iframe at least shows SOMETHING when PDFShift is down. The
      // HTML fallback won't paginate, but it lets the rep see content.
      let blob;
      if (json.pdf_base64) {
        const bytes = Uint8Array.from(atob(json.pdf_base64), c => c.charCodeAt(0));
        blob = new Blob([bytes], { type: 'application/pdf' });
        setFilename(json.name ? `${json.name}.pdf` : 'Service-Order-Preview.pdf');
      } else if (json.html) {
        blob = new Blob([json.html], { type: 'text/html' });
        setFilename(json.name ? `${json.name}.html` : 'Service-Order-Preview.html');
      } else {
        setError('Backend returned no PDF or HTML content.');
        setLoading(false);
        return;
      }

      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setBlobUrl(url);
      setLoading(false);
    } catch (e) {
      setError(e.message || 'Network error');
      setLoading(false);
    }
  };

  // Kick off the build whenever the modal opens with a quoteId.
  useEffect(() => {
    if (open && quoteId) {
      buildPreview();
    }
    // Cleanup: revoke the blob URL when the modal closes (or quoteId changes).
    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
    // v3.5.99.6.2: do NOT add `eslint-disable-next-line react-hooks/exhaustive-deps`
    // here. This project's ESLint config does NOT load the react-hooks plugin,
    // and CRA treats any disable directive referencing an unknown rule as a
    // hard build-failing error. Same trap broke v3.5.27 and v3.5.86.10.1. The
    // useEffect below is semantically correct without the disable comment —
    // buildPreview is a stable closure over the same deps already declared.
  }, [open, quoteId, docType]);

  if (!open) return null;

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 30, 60, 0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 8,
          width: '95vw', height: '92vh', maxWidth: 1280,
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ padding: '12px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0 }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0f1e3c', margin: 0 }}>Service Order Preview</h2>
            <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {clientBiz ? `${clientBiz} · ` : ''}This is exactly what will be sent to the client for signature.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={handleDownload} disabled={!blobUrl} style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600, background: blobUrl ? '#0f1e3c' : '#f3f4f6', color: blobUrl ? 'white' : '#9ca3af', border: 'none', borderRadius: 4, cursor: blobUrl ? 'pointer' : 'not-allowed' }}>
              ⬇ Download
            </button>
            <button onClick={onClose} style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600, background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}>
              Close
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, background: '#f3f4f6', position: 'relative', minHeight: 0 }}>
          {loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24 }}>
              <div style={{ width: 36, height: 36, border: '3px solid #e5e7eb', borderTopColor: '#0f1e3c', borderRadius: '50%', animation: 'sopreview-spin 1s linear infinite' }} />
              <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
                Generating preview…<br/>
                <span style={{ fontSize: 10, color: '#9ca3af' }}>This can take up to 10 seconds while PDFShift renders the document.</span>
              </div>
              <style>{`@keyframes sopreview-spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {error && !loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
              <div style={{ maxWidth: 520, padding: 20, background: errorCode === 'rates_not_locked' ? '#fffbeb' : '#fef2f2', border: `1px solid ${errorCode === 'rates_not_locked' ? '#fde68a' : '#fecaca'}`, borderRadius: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: errorCode === 'rates_not_locked' ? '#92400e' : '#991b1b', marginBottom: 6 }}>
                  {errorCode === 'rates_not_locked' ? '⚠ Rates not locked' : '✗ Preview failed'}
                </div>
                <div style={{ fontSize: 11, color: errorCode === 'rates_not_locked' ? '#92400e' : '#991b1b', lineHeight: 1.5, marginBottom: 12 }}>
                  {errorCode === 'rates_not_locked'
                    ? 'The Service Order requires a locked Out-of-Scope Rate Schedule. Close this preview, scroll to the OOS Rate Schedule card, and click "Accept All Rates". Then reopen the preview.'
                    : error}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {errorCode !== 'rates_not_locked' && (
                    <button onClick={buildPreview} style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600, background: '#991b1b', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Retry</button>
                  )}
                  <button onClick={onClose} style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600, background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}>Close</button>
                </div>
              </div>
            </div>
          )}

          {blobUrl && !loading && !error && (
            <iframe
              src={blobUrl}
              title="Service Order Preview"
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
